import React, { useEffect, useState } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Campaign } from '../../../../model/campaign/Campaign';
import { CustomImageHandler } from '../../../../shared/components/CustomImageHandler';
import EmptyState from '../../../../shared/components/placeholder/EmptyState';
import { getPlannerCardIcons, truncateString } from '../../../../shared/functions/Functions';
import { plannerInfluencer } from '../../../../store/influencer/plannerSlice';
import { IAllStates } from '../../../../store/rootReducer';
import { IPlannerOffers, IRescheduleNotify } from '../dto/IPlanner';
import { InfluencerPlannerService } from '../service';
import { RescheduleCard } from './RescheduleCard';
import CardLayout from '../../../../shared/components/card/CardLayout';
import { Pagination } from '../../../../shared/components/Pagination';
import { useMediaQuery } from '@mui/material';
import { BootstrapBreakpoints } from '../../../../enum/BootstrapBreakpoints';
import TooltipOverlay from '../../../../shared/components/tooltip/Tooltip';
import { ManageService } from '../../../brand/campaign/manage/service';
import { ConfirmationModal } from '../../../../shared/components/modal/ConfirmationModal';
import { ErrorToast, SuccessToast } from '../../../../utils/toasters';
import { PlannerOngoingCampaignsSkeleton } from './PlannerSkeletons';

interface IPlannerCardProps {
    offers: IPlannerOffers;
    isLoading?: boolean;
}
const PlannerCard = ({ offers, isLoading = false }: IPlannerCardProps) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [rescheduleNotify, setRescheduleNotify] = useState<IRescheduleNotify[]>([]);
    const statusesToDisplay = ['scheduled', 'approved'];
    const { filterParams, triggerRescheduleList } = useSelector((state: IAllStates) => state.plannerInfluencer);
    const isNotMobile = useMediaQuery(`(min-width: ${BootstrapBreakpoints.MD}px)`);
    const [isApproveEvent, setisApproveEvent] = useState(false);
    const [eventId, setEventId] = useState<number>();
    const [showConfirmModal, setShowConfirmModal] = useState(false);

    useEffect(() => {
        InfluencerPlannerService.getRescheduleNotify().then(response => {
            setRescheduleNotify(response);
        });
    }, [triggerRescheduleList]);

    const selectCampaignHandler = (campaign: Campaign) => {
        dispatch(plannerInfluencer.actions.setFilterParams({ campaignId: campaign?.id }));
        dispatch(plannerInfluencer.actions.setSelectedCampaign(campaign));
        dispatch(plannerInfluencer.actions.setShouldOpenModal(true));
    };

    const approveOrDeclineRescheduleRequest = async () => {
        const service = isApproveEvent
            ? ManageService.approveRescheduleRequest(eventId)
            : ManageService.declineRescheduleRequest(eventId);
        await service
            .then(response => {
                if (response) {
                    dispatch(plannerInfluencer.actions.setTriggerRescheduleList(!triggerRescheduleList));
                    SuccessToast(
                        t(
                            isApproveEvent
                                ? 'general.toasts.successRescheduled'
                                : 'brand.campaign.manage.declined_request_date'
                        )
                    );
                }
            })
            .catch(err => ErrorToast(err));
    };

    const openConfirmationModalAction = (eventId: number, isApproveAction: boolean) => {
        setEventId(eventId);
        setisApproveEvent(isApproveAction);
        setShowConfirmModal(true);
    };

    return (
        <>
            <ConfirmationModal
                title={t(
                    `influencer.planner.${
                        isApproveEvent ? 'confirm_approve_event_title' : 'confirm_decline_event_title'
                    }`
                )}
                description={t(
                    `influencer.planner.${isApproveEvent ? 'confirm_approve_event' : 'confirm_decline_event'}`
                )}
                show={showConfirmModal}
                closeModal={() => {
                    setShowConfirmModal(false);
                }}
                action={approveOrDeclineRescheduleRequest}
            />

            <div className="mb-3">
                <Button
                    variant="outline-primary"
                    className="w-100"
                    onClick={() =>
                        navigate('/influencer/reporting', {
                            state: filterParams?.campaignId && { campaignId: Number(filterParams?.campaignId) ?? '' },
                        })
                    }
                >
                    &gt;&gt; {t('influencer.planner.sendReportings')}
                </Button>
            </div>
            {rescheduleNotify.map(item => (
                <RescheduleCard key={item?.id} rescheduleData={item} openModalActions={openConfirmationModalAction} />
            ))}
            {isNotMobile && (
                <CardLayout>
                    <div className="mb-3">
                        <h5 className="text-center">{t('influencer.planner.ongoingCampaigns')}</h5>
                    </div>
                    {isLoading ? (
                        <PlannerOngoingCampaignsSkeleton />
                    ) : (
                        <>
                            <div className="bg-muted default-radius p-1 p-md-2">
                                <Row className="align-items-md-end">
                                    <Col xs={6}>
                                        <h5 className="text-primary">{offers?.count}</h5>
                                        <span>{t('general.campaigns')}</span>
                                    </Col>
                                    {statusesToDisplay?.map(status => getPlannerCardIcons({ status, offers, t }))}
                                </Row>
                            </div>
                            <Row className="mt-2">
                                {offers?.data?.length ? (
                                    offers?.data?.map(influencer => {
                                        return (
                                            <React.Fragment key={influencer?.id}>
                                                <Col
                                                    xs={6}
                                                    className="d-flex flex-lg-column flex-xl-row align-items-start word-break my-1 px-0 pl-2"
                                                >
                                                    <TooltipOverlay
                                                        placement="left"
                                                        content={influencer?.campaign?.title}
                                                    >
                                                        <div
                                                            className="d-flex align-items-center"
                                                            onClick={() => selectCampaignHandler(influencer?.campaign)}
                                                        >
                                                            <CustomImageHandler
                                                                altTag="Influencer logo"
                                                                classes="small-rounded-logo"
                                                                thumbnailPath={
                                                                    influencer?.campaign?.campaignImages[0]?.thumbnail
                                                                }
                                                                photoPath={
                                                                    influencer?.campaign?.campaignImages[0]
                                                                        ?.thumbnail ?? null
                                                                }
                                                            />
                                                            <div className="d-flex flex-column ml-2 ">
                                                                <span className="text-dark">
                                                                    {truncateString(
                                                                        influencer?.campaign?.title ?? '-',
                                                                        12
                                                                    )}
                                                                </span>
                                                                {influencer?.isLongTerm && (
                                                                    <div className={'custom-badge long-term'}>
                                                                        {t(
                                                                            `brand.campaign.briefing.overview_card.linked`
                                                                        )}
                                                                    </div>
                                                                )}
                                                                {influencer?.isAddonsOnly && (
                                                                    <div className={'custom-badge additional ml-1'}>
                                                                        {t(
                                                                            'influencer.offer_management.modal.additional'
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </TooltipOverlay>
                                                </Col>
                                                <Col
                                                    xs={3}
                                                    className="px-1 d-flex justify-content-center align-items-center"
                                                >
                                                    <span className="text-info">
                                                        {influencer?.totalPlanned?.scheduled ?? 0} /{' '}
                                                        {influencer?.totalPlanned?.totalNbPosts}
                                                    </span>
                                                </Col>
                                                <Col
                                                    xs={3}
                                                    className="px-1 d-flex justify-content-center align-items-center"
                                                >
                                                    <span className="text-primary">
                                                        {influencer?.totalPlanned?.approved ?? 0} /{' '}
                                                        {influencer?.totalPlanned?.totalNbPosts}
                                                    </span>
                                                </Col>
                                            </React.Fragment>
                                        );
                                    })
                                ) : (
                                    <EmptyState />
                                )}
                            </Row>

                            <div className="d-flex justify-content-end">
                                <Pagination
                                    classWrapper="pb-0"
                                    page={filterParams?.page ?? 1}
                                    totalPages={filterParams?.totalPages}
                                    handlePagination={num =>
                                        dispatch(
                                            plannerInfluencer.actions.setFilterParams({ ...filterParams, page: num })
                                        )
                                    }
                                />
                            </div>
                        </>
                    )}
                </CardLayout>
            )}
        </>
    );
};

export default PlannerCard;
